import { FC, useCallback, useEffect, useState } from 'react';

import { CopyOutlined } from '@ant-design/icons';
import { Button, Divider, Table } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Form, Icon, Label, Modal, Tabs, Typography, message } from 'ui';
import BackLink from 'ui/BackLink';
import MemoizedDateTab from 'ui/date-tab';
import Input from 'ui/input/input';
import { formRules } from 'utils/formRules';

import {
  useCreateEventReferrerLinkMutation,
  useLazyGetEventSalesChannelsQuery,
} from 'services/events/eventsApiService';
import { useLazyGetCurrentUserQuery } from 'services/user-management/userManagementApiService';

import EventPopover from 'containers/event-popover';

import { CURRENCIES_SETTING } from 'constants/core-constants';
import { UserEventType } from 'types/core/userTypes';

import s from './SalesChannel.module.scss';

const SalesChannel: FC = () => {
  const [getCurrentUser, { data: currentUser }] = useLazyGetCurrentUserQuery();

  const [getSalesChannels, { isFetching, data: salesChannels }] =
    useLazyGetEventSalesChannelsQuery();

  const [activeEvent, setActiveEvent] = useState<UserEventType>();

  const [curDateName, setCurDateName] = useState<string>();

  const [CreateEventReferrerLink, { isLoading: isSending }] = useCreateEventReferrerLinkMutation();

  const [currentDateId, setCurrentDateId] = useState<string | undefined>();

  const [form] = Form.useForm();

  const [open, setOpen] = useState(false);

  const [text, setText] = useState<string>('');
  const jsText =
    '<script src="https://api.showshappening.com/Scripts/showshappening.embed.min.js"></script>';

  const showModal = () => {
    setOpen(true);
  };
  const handleOk = () => {
    setOpen(false);
    console.log(curDateName);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  useEffect(() => {
    if (activeEvent) {
      const temp = `<iframe src="https://apishowshappening.azurewebsites.net/buy/tickets/${activeEvent?.eventId}"
      frameborder="0"
      scrolling="yes"
      allowfullscreen="true"
      allowpaymentrequest="true"
      allowtransparency="true"
    ></iframe>`;

      setText(temp);
    }
  }, [activeEvent]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard!');
    } catch (err) {
      message.error('Failed to copy!');
    }
  };

  const handleJSCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsText);
      message.success('Copied to clipboard!');
    } catch (err) {
      message.error('Failed to copy!');
    }
  };

  useEffect(() => {
    if (activeEvent?.eventId && currentDateId) {
      getSalesChannels({
        EventIds: [activeEvent?.eventId],
        DateId: currentDateId === 'allDates' ? '' : currentDateId,
      });
    }
  }, [activeEvent?.eventId, getSalesChannels, currentDateId]);

  const handleSubmit = async (values) => {
    const payload = {
      email: values.email,
      // tag: values.tag,
      eventId: activeEvent?.eventId ?? '',
    };
    const res = await CreateEventReferrerLink(payload);

    if ('data' in res) {
      message.success(`Referrer ${values.email} has been added`);
      setOpen(false);
      form.resetFields();
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'profilePic',
      key: 'profilePic',
      render: (profilePic) => {
        return profilePic ? (
          <img
            src={profilePic}
            alt="saleschannel"
            style={{ width: 40, height: 40, borderRadius: '50%' }}
          />
        ) : (
          <Icon name="user" />
        );
      },
    },
    {
      title: 'First name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (row) => `${row ? row : '-'}`,
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (row) => `${row ? row : '-'}`,
    },
    {
      title: 'Link',
      dataIndex: 'linkName',
      key: 'linkName',
    },

    {
      title: 'Referrer Type',
      dataIndex: 'referrerType',
      key: 'referrerType',
      render: (row) => `${row ? row : '-'}`,
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      key: 'qty',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) => (
        <div>
          {activeEvent?.currency &&
            CURRENCIES_SETTING[activeEvent?.currency?.toUpperCase()]?.symbol}{' '}
          {value}
        </div>
      ),
    },
  ];
  const tabItems = [
    {
      label: 'Referral links',
      key: 'referral_links',
      children: (
        <div>
          <Modal
            open={open}
            onOk={handleOk}
            handleClose={handleCancel}
            closable={true}
            footer={[]}
            maskClosable={false}>
            <Modal.Header>
              <Typography type="h3" weight={700}>
                Add event referral link
              </Typography>
              {/* <div style={{ marginTop: 16, marginBottom: 16 }}>
            <DownloadSwitcher value={isPDF} onChange={setIsPDF} />
          </div> */}
            </Modal.Header>
            <Form className={s.form} onFinish={(values) => handleSubmit(values)} form={form}>
              <div className={s.itemWrapper}>
                <Label>
                  E-mail of the referrer or the email of the promoter account that has a team of
                  referrers
                </Label>
                <Form.Item
                  name="email"
                  validateFirst
                  rules={[
                    formRules.required,
                    formRules.email,
                    {
                      validator: (_, value) => {
                        if (!value || !value.includes('@')) return Promise.resolve();

                        // Custom check for invalid patterns
                        const domainPart = value.split('@')[1] || '';
                        const dotCount = (domainPart.match(/\./g) || []).length;

                        if (dotCount > 1 && domainPart.endsWith('.com')) {
                          return Promise.reject('Invalid email domain');
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <Input name="email" autoComplete="off" placeholder="Enter E-mail" />
                </Form.Item>
              </div>
              {/* <div className={s.itemWrapper}>
                <Label>Tag</Label>
                <Form.Item
                  name="tag"
                  validateFirst
                  rules={[formRules.required, formRules.referrerLink]}>
                  <Input name="tag" autoComplete="off" placeholder="Add tag" />
                </Form.Item>
              </div> */}
              <Divider style={{ margin: 0 }} />
              <div key="batchAction" className={s.modalActions}>
                <Button
                  key="ok"
                  htmlType="submit"
                  type="primary"
                  size="large"
                  loading={isSending}
                  block
                  className={s.addLink}
                  onClick={() => handleSubmit}>
                  ADD
                </Button>
                <Button
                  key="back"
                  size="large"
                  className={s.cancel}
                  type="link"
                  block
                  onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Modal>

          <Button className={s.addLinkButton} type="primary" size="large" onClick={showModal}>
            <Icon name="plus" />
            ADD EVENT REFERRAL LINK
          </Button>
          <Table
            loading={isFetching}
            dataSource={salesChannels ? salesChannels[0]?.salesChannels : []}
            columns={columns}
            scroll={{ x: 'max-content', y: 300 }}
            rowKey="version"
            pagination={{ pageSize: 10 }}
            style={{ marginTop: 16 }}
          />
        </div>
      ),
    },
    {
      label: 'Iframe',
      key: 'iframe',
      children: (
        <div className={s.dateCardTabPane}>
          <Typography type="main" className={s.text}>
            Like this: Copy the code and send to your web developer, tell them our developers say
            hey!
          </Typography>
          <Label>Iframe code</Label>
          <TextArea
            className={s.description}
            color="grey"
            style={{ height: 120, fontFamily: 'monospace', fontSize: 14 }}
            rows={16}
            name="description"
            value={text}
          />
          <Button
            type="primary"
            size="large"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            style={{ marginTop: 16, marginBottom: 32, fontSize: 14 }}>
            COPY CODE
          </Button>
          <Label>JavaScript code</Label>
          <TextArea
            style={{ fontFamily: 'monospace', fontSize: 14, marginBottom: 8 }}
            className={s.description}
            value={jsText}
            color="grey"
            name="description"
          />
          <Typography type="main">
            The above code should be placed before the end of your website HTML body tag.
          </Typography>
          <Button
            type="primary"
            size="large"
            icon={<CopyOutlined />}
            onClick={handleJSCopy}
            style={{ marginTop: 16, marginBottom: 16, fontSize: 14 }}>
            COPY JS CODE
          </Button>
        </div>
      ),
    },
    // {
    //   label: 'Ticket Office',
    //   key: 'ticket_office',
    //   children: <div className={s.dateCardTabPane}>Office</div>,
    // },
  ];

  const handleSetActiveEvent = useCallback((event: UserEventType) => {
    setActiveEvent(event);
  }, []);

  useEffect(() => {
    const storedEvent = localStorage.getItem('currentEvent');
    if (storedEvent) {
      setActiveEvent(JSON.parse(storedEvent));
    }
  }, [currentUser, setActiveEvent]);

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.row}>
        <div className={s.titileHeader}>
          {activeEvent?.eventName && (
            <Typography type="h2" weight={700}>
              &quot;{activeEvent?.eventName ?? ''}&quot;&nbsp;Sales Channels
            </Typography>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <EventPopover handleEvent={handleSetActiveEvent} />
        </div>
      </div>
      {activeEvent && (
        <MemoizedDateTab
          setCurrentDateName={setCurDateName}
          eventId={activeEvent?.eventId}
          setCurrentDateId={setCurrentDateId}
        />
      )}
      <Tabs
        size="large"
        defaultActiveKey="defaultKey"
        tabBarStyle={{ color: '#8D949C' }}
        style={{ width: '100%', marginTop: 24 }}
        items={tabItems}
      />
    </div>
  );
};

export default SalesChannel;
